/**
 * L'onboarding: collegare il gestionale e vedere arrivare i dati.
 *
 * Tipi condivisi fra l'API, che fa l'import e ne scrive l'avanzamento, e la web
 * app, che lo disegna. Stanno qui perché la schermata di caricamento e il job
 * che la alimenta devono chiamare le cose allo stesso modo: un passo che il
 * job chiama «calendario» e il frontend «notti» è un passo che non si accende
 * mai.
 */

/** I passi del primo import, nell'ordine in cui avvengono. */
export const PASSI_IMPORT = [
  "collegamento",
  "immobili",
  "prenotazioni",
  "calendario",
  "conversazioni",
  "analisi",
] as const;

export type PassoImport = (typeof PASSI_IMPORT)[number];

export type StatoPasso = "da_fare" | "in_corso" | "fatto" | "saltato";

export interface AvanzamentoPasso {
  stato: StatoPasso;
  /** Quanti ne ha già fatti: immobili letti, notti scritte… */
  fatti?: number;
  /** Quanti in tutto, quando si sa: per il calendario sono gli immobili. */
  totale?: number;
  /** Perché è stato saltato, detto per intero. */
  nota?: string;
}

export type AvanzamentoImport = Record<PassoImport, AvanzamentoPasso>;

export type StatoImport = "in_corso" | "completato" | "fallito";

/** A che punto è l'onboarding di un account. */
export type PassoOnboarding = "gestionale" | "importazione" | "fatto";

export interface OnboardingView {
  passo: PassoOnboarding;
  /** Il nome dell'attività, per salutare. */
  nomeAttivita: string;
  gestionale: {
    collegato: boolean;
    /** Solo le ultime quattro cifre: il resto non torna mai indietro. */
    clientIdFinale: string | null;
  };
  import: {
    stato: StatoImport | null;
    avanzamento: AvanzamentoImport | null;
    errore: string | null;
    iniziatoIl: string | null;
    finitoIl: string | null;
  };
  /** Il riepilogo per la schermata finale. */
  riepilogo: {
    immobili: number;
    prenotazioni: number;
    notti: number;
    conversazioni: number;
    avvisi: number;
  } | null;
}

/** L'avanzamento vuoto, con tutti i passi da fare. */
export function avanzamentoIniziale(): AvanzamentoImport {
  return Object.fromEntries(
    PASSI_IMPORT.map((passo) => [passo, { stato: "da_fare" }]),
  ) as AvanzamentoImport;
}
